import { createFileRoute, notFound } from '@tanstack/react-router';

import { buildGuards, hasFlagEnabled } from '@mint-lib/routing-guards';
import { Platforms } from '@mint-sdk/api-bff';
import { z } from 'zod';

import { Integration } from '@myn/integrations_v2/routes';

const restrictedPlatforms = {
  guardOmniConnectorCriteo: buildGuards(hasFlagEnabled('omniConnectorCriteo')),
  guardOmniConnectorTradeDesk: buildGuards(
    hasFlagEnabled('omniConnectorTradeDesk'),
  ),
  guardOmniConnectorXads: buildGuards(hasFlagEnabled('omniConnectorXads')),
};

export const Route = createFileRoute(
  '/_secured/external-data/integrations/$integration/$tabName/$type/',
)({
  async beforeLoad({ context, location }) {
    const guardsContext = await context.getGuardContext(
      await context.getInstance('flagsmith', '@mint/arm'),
    );

    const guards = [
      {
        guard: restrictedPlatforms.guardOmniConnectorCriteo,
        pathname: 'criteo_integrated',
      },
      {
        guard: restrictedPlatforms.guardOmniConnectorTradeDesk,
        pathname: 'trade_desk_integrated',
      },
      {
        guard: restrictedPlatforms.guardOmniConnectorXads,
        pathname: 'x_ads_integrated',
      },
    ];

    const pathname = location.pathname;
    const currentLocation = pathname.split('/')[3];

    if (
      guards.some(
        (platformGuard) =>
          !platformGuard.guard(guardsContext) &&
          currentLocation === platformGuard.pathname,
      )
    ) {
      throw notFound();
    }
    return Integration;
  },
  loader: async ({ context, params }) => {
    const i18n = await context.getInstance('i18n', '@mint/routing');
    await context.getInstance('i18n', '@myn/integrations_v2');
    const isSettingsV2 = await context.isFlagEnabled(
      '@mint/arm',
      'settingsMint2',
    );

    const platforms = isSettingsV2
      ? []
      : await context.prefetchQuery({
          ...Platforms.listQueryOptions({
            params: { excludeDeleted: true, excludeNotIntegrated: false },
          }),
          staleTime: 1000 * 60 * 5,
        });

    const platform = platforms.find(
      (platform) => platform.platform === params.integration,
    );

    const title = i18n.t('Integrations - {{platform}} | MINT', {
      platform: platform?.label || params.integration,
    });

    return { title };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData?.title,
      },
    ],
  }),
  validateSearch: z.object({
    state: z.string().optional(),
    id: z.coerce.string().optional(),
    code: z.string().optional(),
    scope: z.string().optional(),
    oauth_token: z.string().optional(),
    oauth_verifier: z.string().optional(),
  }),
});
